<template>
  <div v-if="localBlocks.length" class="promo-banner critical-component-hide">
    <span
      class="visually-hidden"
      v-text="$string('general.site_header.promo_banner')"
    />

    <transition name="fade">
      <splide class="promo-banner__list" :options="carouselOptions">
        <splide-slide
          v-for="block of localBlocks"
          :key="block.key"
          class="promo-banner__list-item"
        >
          <a
            v-if="block.url"
            class="promo-banner__list-item-link text-mobile-paragraph-s-regular"
            :href="block.url"
            v-text="block.text"
          />

          <span
            v-else
            class="promo-banner__list-item-text text-mobile-paragraph-s-regular"
            v-text="block.text"
          />
        </splide-slide>
      </splide>
    </transition>
  </div>
</template>

<script>
/**
 * Vue: Promo banner (promo-banner)
 * -----------------------------------------------------------------------------
 * Promo banner template.
 * - Conditionally loaded when section has blocks using Liquid.
 *
 * @param {Array} blocks - Promo blocks.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} timing - Timing section settings.
 *
 */
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import { convertToHandle } from '~/helpers/convert';
import { dateTime } from '~/helpers/general';

export default {
  name: 'PromoBanner',

  components: {
    Splide,
    SplideSlide,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    timing: {
      type: Number,
      default: 3000,
    },
  },

  data() {
    return {
      activeSlide: 1,
      carouselOptions: {
        arrows: false,
        autoHeight: true,
        autoplay: true,
        interval: this.timing * 1000,
        pagination: false,
        pauseOnFocus: false,
        pauseOnHover: true,
        rewind: true,
        type: 'fade',
      },
      localBlocks: [],
    };
  },

  async mounted() {
    this.localBlocks = this.getLocalBlocks();

    setInterval(() => {
      this.updateBlocks();
    }, 1000);

    await this.$nextTick();
    cnvs.EventBus.emit('cnvs:update-site-header-height');
  },

  methods: {
    /**
     * Update blocks.
     */
    updateBlocks() {
      const newBlocks = this.getLocalBlocks();

      if (newBlocks.length === this.localBlocks.length) {
        return;
      }

      this.localBlocks = newBlocks;
    },

    /**
     * Get local blocks by adding additional details to blocks prop.
     * @returns {Array}
     */
    getLocalBlocks() {
      return this.blocks
        .map((block, index) => {
          if (
            !block.text ||
            (block.type === 'campaign' && !dateTime(block.start, block.end))
          ) {
            return false;
          }

          return {
            ...block,
            key: `promo-${index}-${convertToHandle(block.text)}`,
          };
        })
        .filter(Boolean);
    },
  },
};
</script>

<style lang="scss">
@import './promo-banner';
@import '@/components/splide';
</style>
